/* eslint-disable no-shadow */
import { createSelector } from '@reduxjs/toolkit';

import { logDevMessage, sanitizeSearchText } from 'utils/utils';
import { CARE_CATEGORIES, PLACES_BY_IDS, PROVIDERS_BY_IDS } from 'utils/constants';
import { ENDPOINTS, SEARCH_LIMIT, SEARCH_METHODS, PARAMS } from './fusionConstants';
import * as selectSearch from '../slices/search/selectSearch';
import * as selectNetworks from '../slices/config/selectNetworks';
import * as selectFilters from '../slices/filters/selectFilters';
import * as selectLocation from '../slices/location/selectLocation';
import { apiUrl as selectApiUrl } from '../slices/config/selectConfig';
import { AFFILIATION_TYPES } from '../slices/results/providerConstants';

export const dedupePlacesIndexParam = () =>
  `use_dedupe_places_index=${Boolean(process.env.REACT_APP_USE_DEDUPED_INDEX)}`;

/* ***************************************** */
/* ************ search method ************** */
/* ***************************************** */
export const searchMethod = createSelector(
  [
    selectSearch.type,
    selectSearch.isSingleProviderSearch,
    selectSearch.isSinglePlaceSearch,
    selectSearch.isPlaceByExactNameSearch,
  ],
  (searchType, isSingleProviderSearch, isSinglePlaceSearch, isPlaceByExactNameSearch) => {
    switch (searchType) {
      case CARE_CATEGORIES.PROVIDER_SPECIALTY:
        return SEARCH_METHODS.PROVIDERS_BY_SPECIALTY;
      case CARE_CATEGORIES.PROVIDER_AFFILIATION:
        return SEARCH_METHODS.PROVIDERS_BY_AFFILIATION;
      case CARE_CATEGORIES.PROVIDER_NAME:
        if (isSingleProviderSearch) return SEARCH_METHODS.PROVIDER_BY_ENTITY_ID;
        return SEARCH_METHODS.PROVIDERS_BY_NAME;
      case CARE_CATEGORIES.FACILITY_TYPE:
        return SEARCH_METHODS.PLACES_BY_TYPE;
      case CARE_CATEGORIES.FACILITY_NAME:
        if (isSinglePlaceSearch) return SEARCH_METHODS.PLACE_BY_ID;
        if (isPlaceByExactNameSearch) return SEARCH_METHODS.PLACE_BY_NAME_EXACT;
        return SEARCH_METHODS.PLACES_BY_LOOSE_NAME;
      case CARE_CATEGORIES.PROVIDER_SERVICE:
        return SEARCH_METHODS.PROVIDERS_BY_SERVICE;
      case CARE_CATEGORIES.FACILITY_SERVICE:
        return SEARCH_METHODS.PLACES_BY_SERVICE;
      case PROVIDERS_BY_IDS:
        return SEARCH_METHODS.PROVIDERS_BY_ENTITY_IDS;
      case PLACES_BY_IDS:
        return SEARCH_METHODS.PLACES_BY_ID;
      case CARE_CATEGORIES.PROVIDER_LOCATION:
        return SEARCH_METHODS.PROVIDERS_BY_LOCATION;
      default:
        return undefined;
    }
  }
);

/* ***************************************** */
/* *******+******* endpoint **************** */
/* ***************************************** */
export const endpoint = createSelector(
  [searchMethod, selectSearch.entityId],
  (searchMethod, entityId) => {
    switch (searchMethod) {
      // Providers
      case SEARCH_METHODS.PROVIDERS_BY_SPECIALTY:
      case SEARCH_METHODS.PROVIDERS_BY_NAME:
      case SEARCH_METHODS.PROVIDERS_BY_SERVICE:
      case SEARCH_METHODS.PROVIDERS_BY_ENTITY_IDS:
      case SEARCH_METHODS.PROVIDERS_BY_AFFILIATION:
      case SEARCH_METHODS.PROVIDERS_BY_LOCATION:
        return ENDPOINTS.PROVIDERS;
      case SEARCH_METHODS.PROVIDER_BY_ENTITY_ID:
        return `${ENDPOINTS.PROVIDERS}/${encodeURIComponent(entityId)}`;

      // Places
      case SEARCH_METHODS.PLACES_BY_TYPE:
      case SEARCH_METHODS.PLACES_BY_LOOSE_NAME:
      case SEARCH_METHODS.PLACE_BY_NAME_EXACT:
      case SEARCH_METHODS.PLACES_BY_SERVICE:
      case SEARCH_METHODS.PLACES_BY_ID:
        return ENDPOINTS.PLACES;
      case SEARCH_METHODS.PLACE_BY_ID:
        return `${ENDPOINTS.PLACES}/${encodeURIComponent(entityId)}`;
      default:
        return undefined;
    }
  }
);

/* ***************************************** */
/* ************* query params ************** */
/* ***************************************** */

/* Below is a selector for every potential query param sent to fusion.
 * Each selector should either return a valid query param pair: example networkSlug=bcbsarkansas
 * or and empty string if there should be no query param
 * */

export const networkParam = createSelector([selectNetworks.currentSlug], (currentSlug) => {
  if (!currentSlug) return '';
  return `${PARAMS.NETWORK_SLUG}=${encodeURIComponent(currentSlug)}`;
});

export const limitParam = () => `${PARAMS.LIMIT}=${SEARCH_LIMIT}`; // Currently we don't ever change the limit value (the results per page). In the future we may, and would just need to change this function to a selector.
export const offsetParam = () => `${PARAMS.OFFSET}=0`; // Currently we only start at an offset of 0. Fetches for next pages use the prebuilt url from the response. We never explicitly set a modified offset.

export const orderingParam = createSelector(
  [endpoint, selectFilters.providerSort, selectFilters.placeSort],
  (endpoint, providerSort, placeSort) => {
    switch (endpoint) {
      case ENDPOINTS.PROVIDERS:
        return `${PARAMS.ORDERING}=${providerSort}`;
      case ENDPOINTS.PLACES:
        return `${PARAMS.ORDERING}=${placeSort}`;
      default:
        return '';
    }
  }
);

export const locationParam = createSelector(
  [selectLocation.latLong],
  (coords) => `${PARAMS.LOCATION}=${coords.latitude},${coords.longitude}`
);

/** This returns a string containing the location param, and either the radius or bounding box param */
export const locationAndAreaParams = createSelector(
  [
    locationParam,
    selectFilters.radius,
    selectFilters.isBoundingBoxSearch,
    selectFilters.boundsAsQueryParams,
  ],
  (locationParam, radius, isBoundingBoxSearch, boundsAsQueryParams) => {
    if (isBoundingBoxSearch) {
      const boundsParam = `${PARAMS.BOUNDING_BOX}=${boundsAsQueryParams}`;
      return `${locationParam}&${boundsParam}`;
    }

    return `${locationParam}&${PARAMS.RADIUS}=${radius}mi`;
  }
);

export const qParam = createSelector([selectSearch.text], (searchText) => {
  const sanitizedSearchText = sanitizeSearchText(searchText);
  if (!sanitizedSearchText) return '';
  return `${PARAMS.Q}=${encodeURIComponent(sanitizedSearchText)}`;
});

export const nameExactParam = createSelector([selectSearch.text], (searchText) => {
  if (!searchText) return '';
  return `${PARAMS.NAME_EXACT}=${encodeURIComponent(searchText)}`;
});

export const specialtyIdParam = createSelector([selectSearch.specialtyId], (specialtyId) => {
  if (!specialtyId) return '';
  return `${PARAMS.SPECIALTY_ID}=${specialtyId}`;
});

export const subspecialtyIdParam = createSelector(
  [selectSearch.subspecialtyId],
  (subspecialtyId) => {
    if (!subspecialtyId || subspecialtyId < 1) return '';
    return `${PARAMS.SUBSPECIALTY_ID}=${subspecialtyId}`;
  }
);

export const facilityCategoryParam = createSelector(
  [selectSearch.classificationId, selectSearch.facilityCategory],
  (classificationId, facilityCategory) => {
    if (!classificationId && !facilityCategory) return '';
    return `classification_${facilityCategory}=${classificationId}`;
  }
);

export const freeformParam = createSelector([selectSearch.isFreeFormSearch], (isFreeFormSearch) => {
  if (!isFreeFormSearch) return '';
  return `${PARAMS.FREEFORM}=true`;
});

export const highlightParam = createSelector(
  [selectSearch.isFreeFormSearch],
  (isFreeFormSearch) => {
    if (!isFreeFormSearch) return '';
    return `${PARAMS.HIGHLIGHT}=true`;
  }
);

export const serviceIdParam = createSelector([selectSearch.serviceId], (serviceId) => {
  if (!serviceId) return '';
  return `${PARAMS.SERVICE_ID}=${serviceId}`;
});

export const providerFilterParams = selectFilters.providerFiltersAsQueryParams;
export const placeFilterParams = selectFilters.placeFiltersAsQueryParams;

export const stateParam = createSelector(
  [selectLocation.locationComponents],
  (locationComponents = {}) => {
    const { state = null } = locationComponents;

    if (!state) return '';
    return `${PARAMS.STATE}=${encodeURIComponent(state)}`;
  }
);

export const npiInParam = createSelector([selectSearch.entityIds], (entityIds) => {
  if (!entityIds || entityIds.length === 0) return '';
  return `${PARAMS.NPI_IN}=${entityIds.map(encodeURIComponent).join(',')}`;
});

export const idInParam = createSelector([selectSearch.entityIds], (entityIds) => {
  if (!entityIds || entityIds.length === 0) return '';
  return `${PARAMS.ID_IN}=${entityIds.map(encodeURIComponent).join(',')}`;
});

export const affiliationParam = createSelector(
  [selectSearch.affiliationName, selectSearch.affiliationType],
  (affiliationName, affiliationType) => {
    const affiliationTypeParamMap = {
      [AFFILIATION_TYPES.HOSPITAL]: PARAMS.HOSPITAL_AFFILIATION,
      [AFFILIATION_TYPES.GROUP]: PARAMS.GROUP_AFFILIATION,
    };

    const paramKey = affiliationTypeParamMap[affiliationType];
    if (!paramKey || !affiliationName) return '';

    return `${paramKey}=${encodeURIComponent(affiliationName)}`;
  }
);

/** @deprecated - use addressHashParam instead */
export const locationIdParam = createSelector([selectSearch.locationId], (locationId) => {
  if (!locationId) return '';
  return `${PARAMS.LOCATION_ID}=${locationId}`;
});

export const addressHashParam = createSelector([selectSearch.addressHash], (addressHash) => {
  if (!addressHash) return '';
  return `${PARAMS.ADDRESS_HASH}=${addressHash}`;
});

/* ***************************************** */
/* ***************** urls ****************** */
/* ***************************************** */

export function joinParams(paramsArray = []) {
  // filter out falsy values like empty strings or undefined, then join remaining params with "&"
  return paramsArray.filter((param) => Boolean(param)).join('&');
}

/**
 * Use this function to quickly compose a search URL. It will automatically create you a selector
 * with the baseUrl & endpoint. You just need to determine which queryParam selectors to include in
 * the URL you are composing.
 * @param {Function[]} includedQueryParams An array of query param selectors
 * @returns {Function} Returns the selector built with createSelector
 */
export function composeUrlSelector(includedQueryParams = []) {
  return createSelector(
    [selectApiUrl, endpoint, networkParam, ...includedQueryParams], // every url uses an base url, endpoint, and network queryparam
    (baseUrl, endpoint, ...queryParams) => {
      // verify that there is a base url
      if (!baseUrl) {
        logDevMessage('Missing baseUrl in composeUrlSelector');
        return '';
      }

      // verify that there is a an endpoint
      if (!endpoint) {
        logDevMessage('Missing endpoint in composeUrlSelector');
        return '';
      }

      // verify that there is a network slug, first element in query params should always be network
      if (!queryParams[0]) {
        logDevMessage('Missing network slug in composeUrlSelector');
        return '';
      }

      const querystring = joinParams(queryParams);
      return `${baseUrl}${endpoint}/?${querystring}`;
    }
  );
}

export const providerByEntityIdUrl = composeUrlSelector([]); // this search type uses location params, otherwise we might end up with a provider's location greater than 250 miles away showing
export const placeByIdUrl = composeUrlSelector([dedupePlacesIndexParam]); // this search type does not use any other query params except for dedupePlacesIndexParam

export const providersByNameUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  qParam,
  providerFilterParams,
  freeformParam,
  highlightParam,
]);

export const providersBySpecialtyUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  specialtyIdParam,
  subspecialtyIdParam,
  providerFilterParams,
]);

export const providersByServiceUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  serviceIdParam,
  providerFilterParams,
]);

export const providersByAffiliationUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  affiliationParam,
  providerFilterParams,
]);

export const providersByLocationUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  addressHashParam,
  providerFilterParams,
]);

export const placesByServiceUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  serviceIdParam,
  placeFilterParams,
  dedupePlacesIndexParam,
]);

export const placesByTypeUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  specialtyIdParam,
  subspecialtyIdParam,
  facilityCategoryParam,
  placeFilterParams,
  dedupePlacesIndexParam,
]);

export const placesByLooseNameUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  qParam,
  placeFilterParams,
  freeformParam,
  highlightParam,
  dedupePlacesIndexParam,
]);

export const placesByNameExactUrl = composeUrlSelector([
  limitParam,
  orderingParam,
  locationAndAreaParams,
  nameExactParam,
  placeFilterParams,
  dedupePlacesIndexParam,
]);

export const providersByEntityIdsUrl = composeUrlSelector([npiInParam]);
export const placesByIdsUrl = composeUrlSelector([idInParam, dedupePlacesIndexParam]);

/* ***************************************** */
/* ****** generic search url selector ****** */
/* ***************************************** */

export const searchUrl = createSelector(
  [
    searchMethod,
    providerByEntityIdUrl,
    providersByEntityIdsUrl,
    providersByNameUrl,
    providersBySpecialtyUrl,
    providersByServiceUrl,
    providersByAffiliationUrl,
    placesByServiceUrl,
    placesByTypeUrl,
    placesByNameExactUrl,
    placesByLooseNameUrl,
    placeByIdUrl,
    placesByIdsUrl,
    providersByLocationUrl,
  ],
  (
    searchMethod,
    providerByEntityIdUrl,
    providersByEntityIdsUrl,
    providersByNameUrl,
    providersBySpecialtyUrl,
    providersByServiceUrl,
    providersByAffiliationUrl,
    placesByServiceUrl,
    placesByTypeUrl,
    placesByNameExactUrl,
    placesByLooseNameUrl,
    placeByIdUrl,
    placesByIdsUrl,
    providersByLocationUrl
  ) => {
    switch (searchMethod) {
      case SEARCH_METHODS.PROVIDER_BY_ENTITY_ID:
        return providerByEntityIdUrl;
      case SEARCH_METHODS.PROVIDERS_BY_ENTITY_IDS:
        return providersByEntityIdsUrl;
      case SEARCH_METHODS.PROVIDERS_BY_NAME:
        return providersByNameUrl;
      case SEARCH_METHODS.PROVIDERS_BY_SPECIALTY:
        return providersBySpecialtyUrl;
      case SEARCH_METHODS.PROVIDERS_BY_SERVICE:
        return providersByServiceUrl;
      case SEARCH_METHODS.PROVIDERS_BY_AFFILIATION:
        return providersByAffiliationUrl;
      case SEARCH_METHODS.PLACES_BY_SERVICE:
        return placesByServiceUrl;
      case SEARCH_METHODS.PLACES_BY_TYPE:
        return placesByTypeUrl;
      case SEARCH_METHODS.PLACES_BY_LOOSE_NAME:
        return placesByLooseNameUrl;
      case SEARCH_METHODS.PLACE_BY_NAME_EXACT:
        return placesByNameExactUrl;
      case SEARCH_METHODS.PLACE_BY_ID:
        return placeByIdUrl;
      case SEARCH_METHODS.PLACES_BY_ID:
        return placesByIdsUrl;
      case SEARCH_METHODS.PROVIDERS_BY_LOCATION:
        return providersByLocationUrl;
      default:
        // because this selector gets called inside an async thunk, this Error message gets handled by the resultsSlice reducer and is subsequently displayed to the user.
        throw new Error(
          'Invalid search method. Please select a menu option for "What are you searching for?"'
        );
    }
  }
);

export const serviceUrl = createSelector(
  [selectApiUrl, serviceIdParam, networkParam, stateParam],
  (baseUrl, serviceId, network, state) => {
    if (!serviceId || !network || !state) {
      return '';
    }

    return `${baseUrl}${ENDPOINTS.SERVICE}/?${joinParams([serviceId, network, state])}`;
  }
);

export const searchTermsUrl = createSelector(
  [selectApiUrl],
  (baseUrl) => `${baseUrl}${ENDPOINTS.SEARCH_TERMS}`
);

export const specialtiesUrl = createSelector(
  [selectApiUrl],
  (baseUrl) => `${baseUrl}${ENDPOINTS.SPECIALTIES}/?include_facility_classification=true`
);

export const configUrl = createSelector(
  [selectApiUrl],
  (baseUrl) => `${baseUrl}${ENDPOINTS.CLIENT_CONFIG}`
);

export const memberPlanUrl = createSelector(
  [selectApiUrl],
  (baseUrl) => `${baseUrl}${ENDPOINTS.MEMBER_PLAN}`
);
/**
 * This URL is incomplete without the `type` parameter
 * `&type=__` is appended in the hook that uses this selector `useHandleAutocompleteSuggestions`
 * */
export const autocompleteBaseUrl = createSelector(
  [selectApiUrl, locationParam, networkParam, qParam],
  (baseUrl, location, network, q) => {
    if (!baseUrl || !location || !network || !q) {
      return '';
    }

    const constantParams = ['neighbours=4', 'radius=5'];

    return `${baseUrl}${ENDPOINTS.AUTOCOMPLETE}/?${joinParams([
      location,
      network,
      q,
      ...constantParams,
    ])}`;
  }
);
